import CartVariantPicker, { lineNeedsVariant, variantLabel } from '../../shared/CartVariantPicker';
import { canOnSale } from '../../permissions/access';
import React, { useState, useEffect } from 'react';
import { formatCurrency, calcItemEffectivePrice, calcItemTotal, PAYMENT_TYPE_LABELS, PAYMENT_TYPES } from '../utils/checkoutCalculations';
import { useSellerTaxStore, cartTaxBreakdown } from '../../taxes/sellerTax';
import { DISCOUNTS_VISIBLE } from '../../../config/featureFlags';
import toast from '../../shared/Toast';
import { useManualRule } from '../../discounts/manualRule';
import '../../discounts/till/till.css';

// These payment types are manual records and can be removed
// Loyalty rewards are removable: unlike a voucher redemption, applying one makes
// no API call (the card is only consumed at checkout completion), so dropping the
// row before completing is clean.
const REMOVABLE_PAYMENT_TYPES = [PAYMENT_TYPES.CASH, PAYMENT_TYPES.CARD, PAYMENT_TYPES.OTHER, PAYMENT_TYPES.LOYALTY_REWARD];

// Feature-flagged off until the tip flow is reviewed end-to-end
// (commission split semantics + cashup reporting + how it interacts
// with the new captured-only revenue model).
const SHOW_TIP_OPTIONS = false;

/**
 * SummaryScreen
 * Shows line items, deposits, discounts, tip, totals.
 * Footer is rendered separately by CheckoutPanel (outside scroll area).
 */
const SummaryScreen = ({ state, actions, onCheckout, onClose, currencySymbol }) => {
  // Roles & permissions: discounting someone else's booking needs `discounts`.
  const canDiscount = canOnSale('discounts', [state.booking?.hairdresserId].concat((state.lineItems || []).map((i) => i.staffId)));
  // Selling below the menu: always logged for Reports; refused here only when
  // the owner chose 'refuse' (discounts/manualRule.js — the server checks too).
  const manualRule = useManualRule();
  // Tax lines follow the seller of the booking's stylist — docs/tax-money-path.md §6.
  // Inclusive only: informational, the balance already contains the tax.
  const taxSellerId = (state.lineItems.find((i) => i.staffId) || {}).staffId || state.booking?.hairdresserId || null;
  const sellerTaxStore = useSellerTaxStore(taxSellerId);
  const itemsSubtotal = state.lineItems.reduce((n, i) => n + calcItemTotal(i), 0);
  const cartOnlyDiscount = Math.max(0, (state.discountTotal || 0) - state.lineItems.reduce((n, i) => n + (calcItemTotal({ ...i, discount: null }) - calcItemTotal(i)), 0));
  const taxDiscountFactor = itemsSubtotal > 0 ? Math.max(0, (itemsSubtotal - cartOnlyDiscount) / itemsSubtotal) : 1;
  const taxLines = cartTaxBreakdown(sellerTaxStore, state.lineItems, taxDiscountFactor, calcItemTotal);
  const taxOnTop = taxLines && taxLines.mode === 'exclusive' ? taxLines.total : 0;
  useEffect(() => {
    if (actions.setTax && Math.abs((state.taxAmount || 0) - taxOnTop) >= 0.005) actions.setTax(taxOnTop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxOnTop]);
  const fmt = (amount) => formatCurrency(amount, currencySymbol);

  // Group line items by type (services first, then products)
  const services = [];
  const products = [];
  state.lineItems.forEach((item, index) => {
    const enriched = { ...item, _index: index };
    if (item.type === 'product') {
      products.push(enriched);
    } else {
      services.push(enriched);
    }
  });

  // Check for multi-staff (only relevant if multiple distinct named staff)
  const uniqueStaff = new Set(state.lineItems.map(i => i.staffId).filter(Boolean));
  const isMultiStaff = uniqueStaff.size > 1;

  // Client notes
  const clientNotes = state.client?.note || state.client?.notes || state.booking?.bookingNote || '';

  // Editable price state
  const [editingIndex, setEditingIndex] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  // Which line's pills are open, and the part-made choices per line.
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});

  const startEditing = (index, currentPrice) => {
    setEditingIndex(index);
    setEditPrice(currentPrice.toString());
  };

  const commitEdit = () => {
    if (editingIndex !== null) {
      const val = parseFloat(editPrice);
      // Typing a lower price takes money off, so it needs the Discount
      // permission (else it would get round every code and discount rule)
      const line = (state.lineItems || [])[editingIndex];
      const listPrice = line ? (line.listPrice != null ? line.listPrice : line.originalPrice) : 0;
      if (manualRule === 'refuse' && !isNaN(val) && line && val < listPrice && !canOnSale('discounts', [line.staffId])) {
        toast.error('This salon only lets people with Discounts lower a price on someone else’s sale. Ask the salon owner if you need to.');
        setEditingIndex(null);
        setEditPrice('');
        return;
      }
      if (!isNaN(val) && val > 0) {
        actions.updateLineItemPrice(editingIndex, val);
      }
      setEditingIndex(null);
      setEditPrice('');
    }
  };

  const renderLineItem = (item) => {
    const effectivePrice = calcItemEffectivePrice(item);
    const lineTotal = calcItemTotal(item);
    const qty = item.quantity || 1;
    const hasDiscount = item.discount && effectivePrice < item.originalPrice;
    const isEditing = editingIndex === item._index;

    return (
      <div className={`co-line-item${lineNeedsVariant(item) ? ' co-line-item--needs-variant' : ''}`} key={item._index}>
        {/* Colour strip spans both rows */}
        <div className="co-line-item-color" style={{ backgroundColor: item.diaryColour || '#D6D5EC' }} />

        <div className="co-line-item-content">
          {/* Row 1: name + badges + remove */}
          <div className="co-line-item-row1">
            <div className="co-line-item-name">
              {item.name || 'Unnamed'}
              {item.addedDuringCheckout && <span className="co-line-item-badge co-line-item-badge--added">Added</span>}
            </div>
            <button className="co-remove-btn" onClick={() => actions.removeLineItem(item._index)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>
            </button>
          </div>

          {/* Sub-info */}
          {isMultiStaff && item.staffName && (
            <div className="co-line-item-sub">{item.staffName}</div>
          )}
          {/* Variant resolved in place — identical to the POS cart. */}
          {item.type === 'product' && (item.variants || []).length > 0 && (
            (!item.variantId || editingVariantIndex === item._index) ? (
              <>
                <CartVariantPicker
                  options={item.options}
                  variants={item.variants}
                  selection={variantSelections[item._index] || {}}
                  onChange={(sel) => setVariantSelections(prev => ({ ...prev, [item._index]: sel }))}
                  onResolved={(v) => {
                    actions.updateLineItem(item._index, {
                      variantId: v._id,
                      variantLabel: variantLabel(item.options, v),
                      originalPrice: (v.retailPrice !== null && v.retailPrice !== undefined) ? v.retailPrice : item.originalPrice,
                      stockPrice: (v.stockPrice !== null && v.stockPrice !== undefined) ? v.stockPrice : item.stockPrice,
                    });
                    setEditingVariantIndex(null);
                  }}
                />
                {!item.variantId && <p className="cvp-prompt">Choose {(item.options || []).map(o => o.name).join(' and ')} to sell this</p>}
              </>
            ) : (
              <button type="button" className="cvp-chosen" onClick={() => setEditingVariantIndex(item._index)}>
                {item.variantLabel} · change
              </button>
            )
          )}
          {qty > 1 && (
            <div className="co-line-item-sub">{fmt(effectivePrice)} each</div>
          )}

          {/* Row 2: stepper + price */}
          <div className="co-line-item-row2">
            <div className="co-qty-stepper">
              <button className="co-qty-step" onClick={() => actions.setLineItemQuantity(item._index, qty - 1)} disabled={qty <= 1}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="2" y1="5" x2="8" y2="5"/></svg>
              </button>
              <span className="co-qty-num">{qty}</span>
              <button className="co-qty-step" onClick={() => actions.setLineItemQuantity(item._index, qty + 1)}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="5" y1="2" x2="5" y2="8"/><line x1="2" y1="5" x2="8" y2="5"/></svg>
              </button>
            </div>

            <div className="co-line-item-price">
              {hasDiscount && (
                <span className="co-line-item-original">{fmt(item.originalPrice * qty)}</span>
              )}
              {isEditing ? (
                <div className="co-inline-edit">
                  <span className="co-inline-edit-symbol">{currencySymbol}</span>
                  <input
                    className="co-inline-edit-input"
                    type="number"
                    step="0.01"
                    value={editPrice}
                    onChange={e => setEditPrice(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={e => { if (e.key === 'Enter') commitEdit(); }}
                    autoFocus
                  />
                </div>
              ) : (
                <button className="co-price-btn" onClick={() => startEditing(item._index, effectivePrice)} title="Edit unit price">
                  {fmt(lineTotal)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="co-summary">
      {/* Client notes card */}
      {clientNotes && (
        <div className="co-client-notes">
          <div className="co-section-title">Client Notes</div>
          <p className="co-notes-text">{clientNotes}</p>
        </div>
      )}

      {/* Line items */}
      <div className="co-section">
        <div className="co-section-title">Services & Items</div>

        {/* Services section */}
        {services.length > 0 && products.length > 0 && (
          <div className="co-group-label">Services</div>
        )}
        {services.map(item => renderLineItem(item))}

        {/* Products section */}
        {products.length > 0 && services.length > 0 && (
          <div className="co-group-label" style={{ marginTop: 12 }}>Products</div>
        )}
        {products.map(item => renderLineItem(item))}

        {/* Vouchers being sold */}
        {state.vouchersToSell.map((voucher, i) => (
          <div className="co-line-item" key={`voucher_sell_${i}`}>
            <div className="co-line-item-color" style={{ backgroundColor: '#007EA0' }} />
            <div className="co-line-item-info">
              <div className="co-line-item-name">
                {voucher.name}
                <span className="co-line-item-badge co-line-item-badge--voucher">Voucher</span>
              </div>
              {voucher.recipientName && (
                <div className="co-line-item-staff">For: {voucher.recipientName}</div>
              )}
            </div>
            <div className="co-line-item-price">{fmt(voucher.value)}</div>
            <div className="co-line-item-actions">
              <button
                className="co-remove-btn"
                onClick={() => actions.removeVoucherToSell(i)}
                title="Remove"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#c7c7cc" strokeWidth="1.4" strokeLinecap="round"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>
              </button>
            </div>
          </div>
        ))}

        {state.lineItems.length === 0 && state.vouchersToSell.length === 0 && (
          <p className="co-empty">No items</p>
        )}

        {/* Add item button */}
        <button className="co-add-item-btn" onClick={() => actions.setScreen('addItem')}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#007EA0" strokeWidth="2" strokeLinecap="round">
            <line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          Add service, product or voucher
        </button>
      </div>

      {/* Totals */}
      <div className="co-totals">
        <div className="co-total-row">
          <span>Subtotal</span>
          <span>{fmt(state.subtotal)}</span>
        </div>

        {state.promo ? (
          <button className="co-total-row co-total-row--discount co-total-row--tappable dct-row-code" onClick={() => actions.setScreen('code')}>
            <span className="dct-row-label"><span className="dct-code dct-code--sm">{state.promo.code}</span>{state.promo.said && <span>{state.promo.said}</span>}</span>
            <span>-{fmt(state.discountTotal)}</span>
          </button>
        ) : state.discountTotal > 0 && (
          <button className="co-total-row co-total-row--discount co-total-row--tappable" onClick={() => canDiscount && actions.setScreen('discount')}>
            <span>
              Discount
              {state.cartDiscount?.type === 'percentage' && ` (${state.cartDiscount.value}%)`}
            </span>
            <span>-{fmt(state.discountTotal)}</span>
          </button>
        )}
        {DISCOUNTS_VISIBLE && state.promoDropped && !state.promo && (
          <div className="dct-dropped" role="status">
            <span>{state.promoDropped} came off because the sale changed.</span>
            <button type="button" onClick={() => actions.setScreen('code')}>Add it again</button>
          </div>
        )}

        {taxLines && taxLines.rows.map((r) => (
          taxLines.mode === 'exclusive' ? (
            <div key={r.label} className="co-total-row">
              <span>{r.label}</span>
              <span>+{fmt(r.amount)}</span>
            </div>
          ) : (
            <div key={r.label} className="co-total-row" style={{ color: '#8e8e93', fontSize: 12.5 }}>
              <span>Includes {r.label}</span>
              <span>{fmt(r.amount)}</span>
            </div>
          )
        ))}
        {state.tipAmount > 0 && (
          <button className="co-total-row co-total-row--tip co-total-row--tappable" onClick={() => actions.setScreen('tip')}>
            <span>Tip ({state.tip.type === 'cash' ? 'Cash' : 'Card'})</span>
            <span>{fmt(state.tipAmount)}</span>
          </button>
        )}

        {/* Existing transactions (deposits + prior payments) */}
        {state.existingTransactions.map((t, i) => (
          <div className="co-total-row co-total-row--deposit" key={`existing_${i}`}>
            <span>{t.isDeposit ? 'Deposit' : t.label || PAYMENT_TYPE_LABELS[t.paymentType] || 'Prior payment'}</span>
            <span>-{fmt(t.amount)}</span>
          </div>
        ))}

        {/* Session transactions */}
        {state.transactions.map((t, i) => {
          const canRemove = REMOVABLE_PAYMENT_TYPES.includes(t.paymentType);
          return (
            <div className="co-total-row co-total-row--paid" key={i}>
              <span>{PAYMENT_TYPE_LABELS[t.paymentType] || 'Payment'}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                -{fmt(t.amount)}
                {canRemove && (
                  <button
                    className="co-remove-btn"
                    onClick={() => actions.removeTransaction(i)}
                    style={{ width: 20, height: 20 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#ef4444" strokeWidth="1.6" strokeLinecap="round"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>
                  </button>
                )}
              </span>
            </div>
          );
        })}

        <div className="co-total-row co-total-row--balance">
          <span>Balance</span>
          <span className={state.balanceRemaining > 0 ? 'co-balance--due' : 'co-balance--paid'}>
            {state.balanceRemaining > 0 ? fmt(state.balanceRemaining) : 'Paid'}
          </span>
        </div>

        {/* Inline tip prompt when fully paid and no tip added.
            Skip if a card reader transaction exists — the device already prompted.
            Currently feature-flagged off (see SHOW_TIP_OPTIONS at top of file). */}
        {SHOW_TIP_OPTIONS && state.balanceRemaining <= 0 && state.tipAmount === 0 && state.transactions.length > 0
          && !state.transactions.some(t => t.paymentType === PAYMENT_TYPES.CARD_READER) && (
          <button className="co-tip-prompt" onClick={() => actions.setScreen('tip')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 14.5S1.5 10.5 1.5 6.5C1.5 4.5 3 3 5 3c1.2 0 2.3.6 3 1.5C8.7 3.6 9.8 3 11 3c2 0 3.5 1.5 3.5 3.5 0 4-6.5 8-6.5 8z" stroke="#6366f1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Add a tip?</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 'auto' }}><path d="M5 3L9 7L5 11" stroke="#6366f1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * SummaryFooter
 * Rendered by CheckoutPanel outside the scroll area, pinned to bottom.
 * Uses bdp- classes to match BookingDetailPanel footer exactly.
 */
export const SummaryFooter = ({ state, actions, onCheckout, onClose, currencySymbol }) => {
  const canDiscount = canOnSale('discounts', [state.booking?.hairdresserId].concat((state.lineItems || []).map((i) => i.staffId)));
  // A product with variants and none chosen cannot be sold: we would not know
  // what left the shelf. Mirrors the POS cart footer.
  const unresolvedVariantLines = (state.lineItems || []).filter(lineNeedsVariant).length;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef(null);
  const fmt = (amount) => formatCurrency(amount, currencySymbol);
  const isFullyPaid = state.balanceRemaining <= 0;
  const hasCardReaderTxn = state.transactions.some(t => t.paymentType === PAYMENT_TYPES.CARD_READER);

  const handlePrimaryAction = () => {
    if (isFullyPaid) {
      actions.markComplete();
    } else {
      onCheckout();
    }
  };

  // Close menu on outside click
  React.useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div className="bdp-footer">
      <div className="bdp-menu-wrap" ref={menuRef}>
        <button className="bdp-btn bdp-btn--secondary bdp-btn--menu" onClick={() => setMenuOpen(!menuOpen)}>
          <span>More Options</span>
          <svg className={`bdp-menu-chevron ${menuOpen ? 'bdp-menu-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {menuOpen && (
          <div className="bdp-menu">
            {SHOW_TIP_OPTIONS && !hasCardReaderTxn && (
              <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('tip'); }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 14.5S1.5 10.5 1.5 6.5C1.5 4.5 3 3 5 3c1.2 0 2.3.6 3 1.5C8.7 3.6 9.8 3 11 3c2 0 3.5 1.5 3.5 3.5 0 4-6.5 8-6.5 8z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add Tip
              </button>
            )}
            {DISCOUNTS_VISIBLE && <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('code'); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
              </svg>
              {state.promo ? <>Discount code<span className="dct-code dct-code--sm dct-menu-chip">{state.promo.code}</span></> : 'Add a code'}
            </button>}
            {canDiscount && <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('discount'); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 6L8 10L4 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              Add Discount
            </button>}
            <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('deposit'); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M5 4V2.5C5 1.67 5.67 1 6.5 1h3C10.33 1 11 1.67 11 2.5V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              Take Deposit
            </button>
            <div className="bdp-menu-divider" />
            <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); if (onClose) onClose(); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              Close
            </button>
          </div>
        )}
      </div>
      <button
        className="bdp-btn bdp-btn--primary"
        onClick={handlePrimaryAction}
        disabled={(state.lineItems.length === 0 && state.vouchersToSell.length === 0) || unresolvedVariantLines > 0}
        title={unresolvedVariantLines > 0 ? 'Choose an option on every product first' : undefined}
      >
        {unresolvedVariantLines > 0
          ? `Choose ${unresolvedVariantLines === 1 ? 'an option' : 'options'} to pay`
          : isFullyPaid ? 'Complete' : `Pay ${fmt(state.balanceRemaining)}`}
      </button>
    </div>
  );
};

export default SummaryScreen;
